import {WizKnownError} from '@/sdk/common/error';
import assert from 'assert';
import {feLogger, reportTokenRetryError} from '@/buried';
import {WizRequestOptions, TokenKey} from '../../interface';
import * as WizRequest from '../common/request';
import {ERROR_CODE} from '../common/const';
import {IAddFrontTraceInfo} from '../common/addFrontTraceInfo';

type Handler = ((error?: WizKnownError, mergedRequestType?: string) => Promise<string | {
    wikiToken: string;
    wikiAccessToken: string;
}>) | null;

class ServerBase {
    tokenKey: TokenKey = 'wikiToken';

    invalidTokenHandler: Handler = null;

    addFrontTraceInfo: IAddFrontTraceInfo | null = null;

    constructor(tokenKey: TokenKey) {
        this.tokenKey = tokenKey;
    }

    setInvalidTokenHandler(handler: Handler) {
        this.invalidTokenHandler = handler;
    }

    async refreshToken(error: WizKnownError, mergedRequestType?: string) {
        if (this.invalidTokenHandler) {
            const token = await this.invalidTokenHandler(error, mergedRequestType);
            if (token) {
                if (typeof token === 'string') {
                    this.onTokenUpdated(token);
                } else if (token[this.tokenKey]) {
                    this.onTokenUpdated(token[this.tokenKey]);
                }

            }
            return token;
        }
        return null;
    }

    onTokenUpdated(token: string) {
        assert(token);
    }

    async request(options: WizRequestOptions) {
        try {
            const ret = await WizRequest.standardRequest(options, this.addFrontTraceInfo);
            return ret;
        } catch (error) {
            if (options.noAutoLogin || options.ignoreUgateTokenInvalid) {
                throw error;
            }
            const isTokenInvalid = [
                ERROR_CODE.uuapTokenInvalid,
                ERROR_CODE.ugateTokenInvalid,
                ERROR_CODE.wikiTokenInvalid,
                ERROR_CODE.accessTokenInvalid,
            ].includes(error.code);

            if (!isTokenInvalid) {
                throw error;
            }
            if (options.noRetry) {
                throw error;
            }
            const token = await this.refreshToken(error, options.mergedRequestType);
            if (!token) {
                throw error;
            }
            let tokenOptions: WizRequestOptions = {noRetry: true};
            if (typeof token === 'string') {
                tokenOptions[this.tokenKey] = token;
            } else if (token.wikiAccessToken || token.wikiToken) {
                tokenOptions = {
                    ...tokenOptions,
                    ...token,
                };
            }
            const oldOptions = {...options}; // 存储重试前的传参，用于上报错误信息
            const newOptions = Object.assign(options, tokenOptions);

            try {
                const ret = await WizRequest.standardRequest(newOptions, this.addFrontTraceInfo);
                return ret;
            } catch (retryError) {
                // 重试后依然失败，上报错误信息
                // 暂时仅上报 wikiTokenInvalid、accessTokenInvalid 两个场景
                if ([ERROR_CODE.wikiTokenInvalid, ERROR_CODE.accessTokenInvalid].includes(retryError?.code)) {
                    const reportObj = {
                        url: newOptions.url,
                        code: retryError?.code,
                        message: retryError?.message,
                        wikiAccessToken: newOptions?.wikiAccessToken,
                        wikiToken: newOptions?.wikiToken,
                        originAccessToken: oldOptions?.wikiAccessToken,
                        originWikiToken: oldOptions?.wikiToken,
                        v: 1,
                    };

                    feLogger(reportTokenRetryError(reportObj));
                }
            }
        }
    }
}

export const serverBaseObj = new ServerBase('wikiToken');

export default ServerBase;
